import {
  RushingStats,
  SeasonGameLog,
  TeamOverviewStats,
  PassingStats,
  ReceivingStats,
  DefenseStats,
} from "../../../types/LogTypes";

const getEmptyStats = (): TeamOverviewStats => ({
  club: null as any, // This will be set by the component
  points: 0,
  total_yards: 0,
  rushing_yards: 0,
  passing_yards: 0,
  first_downs: 0,
  penalty_count: 0,
  penalty_yards: 0,
  third_downs_conv: 0,
  fourth_downs_conv: 0,
  rushing_attempts: 0,
  rushing_yards_per_attempt: 0,
  passing_attempts: 0,
  passing_yards_per_attempt: 0,
  tackles_for_loss: 0,
  field_goal_attempts: 0,
  field_goals_made: 0,
  pat_attempts: 0,
  pat_made: 0,
  two_point_conversion_attempts: 0,
  two_point_conversions_made: 0,
  time_of_possession: "00:00",
  turnovers: 0,
  takeaways: 0,
});

export const calculateSeasonStats = (
  gameLogs: SeasonGameLog[] | null
): {
  userClubStats: TeamOverviewStats;
  opponentClubStats: TeamOverviewStats;
  passingStats: PassingStats;
  rushingStats: RushingStats;
  receivingStats: ReceivingStats;
  defenseStats: DefenseStats;
} => {
  if (!gameLogs || gameLogs.length === 0) {
    return {
      userClubStats: getEmptyStats(),
      opponentClubStats: getEmptyStats(),
      passingStats: {},
      rushingStats: {},
      receivingStats: {},
      defenseStats: {},
    };
  }

  const userClubStats = getEmptyStats();
  const opponentClubStats = getEmptyStats();
  const passingStats: PassingStats = {};
  const rushingStats: RushingStats = {};
  const receivingStats: ReceivingStats = {};
  const defenseStats: DefenseStats = {};

  // Sum up all the stats from each game
  gameLogs.forEach((gameLog) => {
    const { user_club, opponent_club, passing, rushing, receiving, defense } =
      gameLog.stats;

    // Just sum up every stat
    userClubStats.points += user_club.points;
    userClubStats.total_yards += user_club.total_yards;
    userClubStats.rushing_yards += user_club.rushing_yards;
    userClubStats.passing_yards += user_club.passing_yards;
    userClubStats.first_downs += user_club.first_downs;
    userClubStats.penalty_count += user_club.penalty_count;
    userClubStats.penalty_yards += user_club.penalty_yards;
    userClubStats.turnovers += user_club.turnovers;
    userClubStats.takeaways += user_club.takeaways;
    userClubStats.third_downs_conv += user_club.third_downs_conv;
    userClubStats.fourth_downs_conv += user_club.fourth_downs_conv;
    userClubStats.rushing_attempts += user_club.rushing_attempts;
    userClubStats.rushing_yards_per_attempt =
      userClubStats.rushing_yards / userClubStats.rushing_attempts;
    userClubStats.passing_attempts += user_club.passing_attempts;
    userClubStats.passing_yards_per_attempt =
      userClubStats.passing_yards / userClubStats.passing_attempts;
    userClubStats.tackles_for_loss += user_club.tackles_for_loss;
    userClubStats.field_goal_attempts += user_club.field_goal_attempts;
    userClubStats.field_goals_made += user_club.field_goals_made;
    userClubStats.pat_attempts += user_club.pat_attempts;
    userClubStats.pat_made += user_club.pat_made;
    userClubStats.two_point_conversion_attempts +=
      user_club.two_point_conversion_attempts;
    userClubStats.two_point_conversions_made +=
      user_club.two_point_conversions_made;
    userClubStats.time_of_possession = "00:00"; // Not implemented yet

    opponentClubStats.points += opponent_club.points;
    opponentClubStats.total_yards += opponent_club.total_yards;
    opponentClubStats.rushing_yards += opponent_club.rushing_yards;
    opponentClubStats.passing_yards += opponent_club.passing_yards;
    opponentClubStats.first_downs += opponent_club.first_downs;
    opponentClubStats.penalty_count += opponent_club.penalty_count;
    opponentClubStats.penalty_yards += opponent_club.penalty_yards;
    opponentClubStats.turnovers += opponent_club.turnovers;
    opponentClubStats.takeaways += opponent_club.takeaways;
    opponentClubStats.third_downs_conv += opponent_club.third_downs_conv;
    opponentClubStats.fourth_downs_conv += opponent_club.fourth_downs_conv;
    opponentClubStats.rushing_attempts += opponent_club.rushing_attempts;
    opponentClubStats.rushing_yards_per_attempt =
      opponentClubStats.rushing_yards / opponentClubStats.rushing_attempts;
    opponentClubStats.passing_attempts += opponent_club.passing_attempts;
    opponentClubStats.passing_yards_per_attempt =
      opponentClubStats.passing_yards / opponentClubStats.passing_attempts;
    opponentClubStats.tackles_for_loss += opponent_club.tackles_for_loss;
    opponentClubStats.field_goal_attempts += opponent_club.field_goal_attempts;
    opponentClubStats.field_goals_made += opponent_club.field_goals_made;
    opponentClubStats.pat_attempts += opponent_club.pat_attempts;
    opponentClubStats.pat_made += opponent_club.pat_made;
    opponentClubStats.two_point_conversion_attempts +=
      opponent_club.two_point_conversion_attempts;
    opponentClubStats.two_point_conversions_made +=
      opponent_club.two_point_conversions_made;
    opponentClubStats.time_of_possession = "00:00"; // Not implemented yet

    // Sum up the passing stats
    if (passing) {
      Object.entries(passing).forEach(([playerId, stats]) => {
        if (!passingStats[playerId]) {
          passingStats[playerId] = {
            yards: 0,
            touchdowns: 0,
            interceptions: 0,
            attempts: 0,
            completions: 0,
            passer_rating: 0,
            completion_percentage: 0,
          };
        }

        const playerStats = passingStats[playerId];
        playerStats.yards += stats.yards;
        playerStats.touchdowns += stats.touchdowns;
        playerStats.interceptions += stats.interceptions;
        playerStats.attempts += stats.attempts;
        playerStats.completions += stats.completions;

        // Calculate completion percentage
        if (playerStats.attempts > 0) {
          playerStats.completion_percentage =
            (playerStats.completions / playerStats.attempts) * 100;
        }

        // Calculate passer rating using NFL formula
        if (playerStats.attempts > 0) {
          const a = (playerStats.completions / playerStats.attempts - 0.3) * 5;
          const b = (playerStats.yards / playerStats.attempts - 3) * 0.25;
          const c = (playerStats.touchdowns / playerStats.attempts) * 20;
          const d =
            2.375 - (playerStats.interceptions / playerStats.attempts) * 25;

          playerStats.passer_rating = Math.max(
            0,
            Math.min(158.3, ((a + b + c + d) / 6) * 100)
          );
        }
      });
    }

    // Sum up the rushing stats
    if (rushing) {
      Object.entries(rushing as RushingStats).forEach(([playerId, stats]) => {
        if (!rushingStats[playerId]) {
          rushingStats[playerId] = {
            yards: 0,
            touchdowns: 0,
            fumbles: 0,
            carries: 0,
            yards_per_carry: 0,
          };
        }

        const playerStats = rushingStats[playerId];
        playerStats.yards += stats.yards;
        playerStats.touchdowns += stats.touchdowns;
        playerStats.fumbles += stats.fumbles;
        playerStats.carries += stats.carries;

        if (playerStats.carries > 0) {
          playerStats.yards_per_carry = playerStats.yards / playerStats.carries;
        }
      });
    }
    // Sum up the receiving stats
    if (receiving) {
      Object.entries(receiving as ReceivingStats).forEach(
        ([playerId, stats]) => {
          if (!receivingStats[playerId]) {
            receivingStats[playerId] = {
              receptions: 0,
              targets: 0,
              yards: 0,
              touchdowns: 0,
              fumbles: 0,
              yards_per_reception: 0,
              catch_percentage: 0,
            };
          }

          const playerStats = receivingStats[playerId];
          playerStats.receptions += stats.receptions;
          playerStats.targets += stats.targets;
          playerStats.yards += stats.yards;
          playerStats.touchdowns += stats.touchdowns;
          playerStats.fumbles += stats.fumbles;

          if (playerStats.receptions > 0) {
            playerStats.yards_per_reception =
              playerStats.yards / playerStats.receptions;
          }
          if (playerStats.targets > 0) {
            playerStats.catch_percentage =
              (playerStats.receptions / playerStats.targets) * 100;
          }
        }
      );
    }
    if (defense) {
      Object.entries(defense as DefenseStats).forEach(([playerId, stats]) => {
        if (!defenseStats[playerId]) {
          defenseStats[playerId] = {
            solo_tackles: 0,
            assisted_tackles: 0,
            tackles_for_loss: 0,
            fumbles_recovered: 0,
            interceptions: 0,
          };
        }
        const playerStats = defenseStats[playerId];
        playerStats.solo_tackles += stats.solo_tackles;
        playerStats.assisted_tackles += stats.assisted_tackles;
        playerStats.tackles_for_loss += stats.tackles_for_loss;
        playerStats.fumbles_recovered += stats.fumbles_recovered;
        playerStats.interceptions += stats.interceptions;
      });
    }
  });

  return {
    userClubStats,
    opponentClubStats,
    passingStats,
    rushingStats,
    receivingStats,
    defenseStats,
  };
};
